/**
 * AI Context Types - Phase 1.1: Enhanced Context Structure
 *
 * These types define the rich, hierarchical context passed to AI for component modifications.
 * Enables commands like "center in parent", "match width of component above", etc.
 */

import { ComponentType } from './index';
import type { AiBindingContextSummary } from '@/services/ai/BindingContextSummary';

/**
 * Parent container context - enables relative positioning commands
 */
export interface ParentContext {
  id: string;
  type: ComponentType | string;
  name?: string;
  layout: {
    display: 'flex' | 'grid' | 'block';
    flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
    justifyContent?: string;
    alignItems?: string;
    gap?: string | number;
    padding?: string | number;
  };
  dimensions: {
    width: number;
    height: number;
  };
  childCount: number;
  childIndex: number; // This component's index among siblings
}

/**
 * Sibling component context - enables relative sizing/positioning
 */
export interface SiblingContext {
  id: string;
  type: ComponentType | string;
  name?: string;
  size: {
    width: number;
    height: number;
  };
  position: {
    x: number;
    y: number;
  };
  relationship: 'before' | 'after';
  distanceFromComponent: number; // Pixel distance
}

/**
 * Viewport and responsive context
 */
export interface ViewportContext {
  currentBreakpoint: 'mobile' | 'tablet' | 'desktop';
  viewportWidth: number;
  viewportHeight: number;
  viewportName: string;
  zoom: number;
  userZoom: number;
  viewportScale: number;
}

/**
 * Layer/z-index context for overlapping detection
 */
export interface LayerContext {
  zIndex: number;
  componentsAbove: string[]; // Component IDs with higher z-index
  componentsBelow: string[]; // Component IDs with lower z-index
  isOverlapping: boolean;
  overlappingComponents: string[]; // IDs of components this overlaps with
}

/**
 * Enhanced component context - combines all context types
 */
export interface EnhancedComponentContext {
  component: {
    id: string;
    type: ComponentType | string;
    name?: string;
    properties: Record<string, unknown>;
    bindingContext?: AiBindingContextSummary;
    style: Record<string, unknown>;
    size: {
      width: number;
      height: number;
    };
    position: {
      x: number;
      y: number;
      z?: number;
    };
    // Template metadata
    semanticRole?: string;
    templateInstanceId?: string;
  };
  parent?: ParentContext;
  siblings?: SiblingContext[];
  viewport: ViewportContext;
  layering: LayerContext;
}

/**
 * Compressed context for token efficiency (only relevant properties)
 */
export interface CompressedComponentContext {
  component: {
    id: string;
    type: string;
    name?: string;
    // Only include properties relevant to the command
    relevantProperties: Record<string, unknown>;
    size: { width: number; height: number };
    position: { x: number; y: number };
  };
  parent?: {
    type: string;
    layout: string;
    dimensions: { width: number; height: number };
    childIndex: number;
    childCount: number;
  };
  siblings?: Array<{
    name?: string;
    type: string;
    size: { width: number; height: number };
    relationship: 'before' | 'after';
  }>;
  viewport: {
    breakpoint: string;
    width: number;
    height: number;
  };
  layer?: {
    zIndex: number;
    hasOverlaps: boolean;
  };
}

/**
 * Context collection options
 */
export interface ContextCollectionOptions {
  includeParent?: boolean;
  includeSiblings?: boolean;
  includeViewport?: boolean;
  includeLayering?: boolean;
  maxSiblings?: number; // Default: 5
  compressForTokens?: boolean;
}

// ============================================================================
// Phase 1.2: Conversational Clarification System Types
// ============================================================================

/**
 * Types of clarification that can be requested
 */
export type ClarificationType =
  | 'component_selection'  // Multiple components match a reference
  | 'value_selection'      // Relative value needs specifics (e.g., "bigger" -> how much?)
  | 'breakpoint_selection' // Breakpoint ambiguity (e.g., "on small screens")
  | 'property_selection'   // Which property to modify (e.g., "make it bigger" for non-text)
  | 'confirmation';        // Confirm a potentially destructive change

/**
 * An option presented to the user during clarification
 */
export interface ClarificationOption {
  id: string;
  label: string;
  description?: string;
  preview?: PropertyChangePreview;
  isRecommended?: boolean;
  metadata?: Record<string, unknown>;
}

/**
 * Preview of property changes for an option
 */
export interface PropertyChangePreview {
  properties?: Record<string, unknown>;
  style?: Record<string, unknown>;
  size?: { width?: number | string; height?: number | string };
}

/**
 * A request for clarification from the user
 */
export interface ClarificationRequest {
  id: string;
  type: ClarificationType;
  question: string;
  options: ClarificationOption[];
  allowCustomInput?: boolean;
  customInputPlaceholder?: string;
  context: ClarificationContext;
  createdAt: Date;
}

/**
 * Context about the original command that triggered clarification
 */
export interface ClarificationContext {
  originalCommand: string;
  detectedIntent: string;
  targetComponentId?: string;
  targetComponentType?: string;
  matchedComponents?: MatchedComponent[];
  pendingChanges?: PropertyChangePreview;
  currentValues?: Record<string, unknown>;
}

/**
 * A component that matched a reference during clarification
 */
export interface MatchedComponent {
  id: string;
  name?: string;
  type: string;
  confidence: number;
  matchReason: string;
  semanticRole?: string;
}

/**
 * User's response to a clarification request
 */
export interface ClarificationResponse {
  requestId: string;
  selectedOptionId: string;
  customValue?: string;
  timestamp: Date;
}

/**
 * Result of ambiguity detection
 */
export interface AmbiguityDetectionResult {
  isAmbiguous: boolean;
  type?: ClarificationType;
  reason?: string;
  matchedComponents?: MatchedComponent[];
  suggestedOptions?: ClarificationOption[];
  confidence: number;
}

/**
 * Patterns for detecting different types of ambiguity
 */
export interface AmbiguityPattern {
  id: string;
  type: ClarificationType;
  patterns: RegExp[];
  action: string;
  priority: number;
}

/**
 * Value suggestion for numeric/relative commands
 */
export interface ValueSuggestion {
  label: string;
  value: number | string;
  description?: string;
  isRelative?: boolean;
  multiplier?: number;
}

/**
 * Configuration for the clarification service
 */
export interface ClarificationServiceConfig {
  maxOptionsToShow: number;
  enableComponentMatching: boolean;
  enableValueSuggestions: boolean;
  enableBreakpointClarification: boolean;
  confidenceThreshold: number;
}

/**
 * History entry for tracking clarifications
 */
export interface ClarificationHistoryEntry {
  request: ClarificationRequest;
  response: ClarificationResponse;
  appliedChanges?: PropertyChangePreview;
  wasSuccessful: boolean;
}

// ============================================================================
// Phase 2.1: Semantic Reference Resolution Types
// ============================================================================

/**
 * Match type indicating how a component reference was resolved
 */
export type SemanticMatchType =
  | 'exact'       // Exact name/ID match
  | 'fuzzy'       // Fuzzy string matching (Levenshtein)
  | 'semantic'    // Semantic role match (_semanticRole field)
  | 'positional'  // Position-based match (above, below, first, etc.)
  | 'alias'       // Matched via vocabulary alias
  | 'contextual'; // Context-based match (selected, recently added)

/**
 * A resolved semantic reference from natural language to component
 */
export interface SemanticReference {
  /** The original reference text from user input */
  referenceText: string;
  /** The resolved component ID */
  resolvedComponentId: string;
  /** Confidence score (0-1) of the resolution */
  confidence: number;
  /** How the reference was matched */
  matchType: SemanticMatchType;
  /** Alternative matches if confidence is below threshold */
  alternatives?: ComponentMatch[];
  /** Debug info about the resolution path */
  resolutionPath?: string;
}

/**
 * A component that matches a reference with scoring metadata
 */
export interface ComponentMatch {
  /** Component ID */
  id: string;
  /** Component name (user-visible) */
  name: string;
  /** Component type */
  type: ComponentType | string;
  /** Why this component matched */
  matchReason: string;
  /** Match confidence (0-1) */
  confidence: number;
  /** Semantic role if available */
  semanticRole?: string;
  /** Position for positional matching */
  position?: {
    x: number;
    y: number;
    z?: number;
  };
}

/**
 * Result of multi-reference resolution attempt
 */
export interface ReferenceResolutionResult {
  /** Whether resolution was successful (single high-confidence match) */
  success: boolean;
  /** The primary resolved reference if successful */
  primaryMatch?: SemanticReference;
  /** All potential matches ordered by confidence */
  allMatches: ComponentMatch[];
  /** Whether clarification is needed (multiple close matches) */
  needsClarification: boolean;
  /** Suggested clarification options if needed */
  clarificationOptions?: ClarificationOption[];
  /** Raw confidence scores for debugging */
  confidenceScores?: Map<string, number>;
}

/**
 * Reference vocabulary entry for alias mapping
 */
export interface VocabularyEntry {
  /** Primary canonical name */
  canonical: string;
  /** Alternative names/aliases that map to this */
  aliases: string[];
  /** Component types this vocabulary applies to */
  applicableTypes?: (ComponentType | string)[];
  /** Weight for scoring (higher = more likely match) */
  weight?: number;
}

/**
 * Cache entry for resolved references
 */
export interface ReferenceCacheEntry {
  /** The original reference text */
  reference: string;
  /** Resolved component ID */
  componentId: string;
  /** Confidence at time of caching */
  confidence: number;
  /** When this was cached */
  cachedAt: number;
  /** Page/context this resolution is valid for */
  pageId?: string;
}

/**
 * Configuration for reference resolution
 */
export interface ReferenceResolutionConfig {
  /** Minimum confidence to consider a match valid (default: 0.7) */
  confidenceThreshold: number;
  /** Maximum fuzzy distance for typo tolerance (default: 3) */
  maxFuzzyDistance: number;
  /** Enable positional references (above, below, etc.) */
  enablePositionalReferences: boolean;
  /** Enable semantic role matching */
  enableSemanticRoles: boolean;
  /** Enable vocabulary alias matching */
  enableAliases: boolean;
  /** Cache TTL in milliseconds (default: 60000 = 1 minute) */
  cacheTTL: number;
  /** Maximum alternatives to return */
  maxAlternatives: number;
}

// ============================================================================
// Phase 2.3: Style Cloning Types
// ============================================================================

/**
 * Scope of styles to clone from a source component
 */
export type StyleCloneScope =
  | 'all'        // Clone all applicable styles
  | 'colors'     // Clone color-related styles only
  | 'typography' // Clone font-related styles only
  | 'spacing'    // Clone padding, margin, gap only
  | 'borders'    // Clone border-related styles only
  | 'shadows'    // Clone shadow-related styles only
  | 'layout';    // Clone layout-related styles only

/**
 * Request to clone styles from a source component to a target
 */
export interface StyleCloneRequest {
  /** Reference text for source component (e.g., "the header", "submit button") */
  sourceRef: string;
  /** Target component ID to apply styles to */
  targetComponentId: string;
  /** Target component type (for compatibility filtering) */
  targetComponentType: ComponentType | string;
  /** Scope of properties to clone */
  propertyScope: StyleCloneScope;
  /** Optional context for resolution */
  context?: {
    selectedComponents?: Array<{ id: string; type: string; name?: string }>;
    templateInstanceId?: string;
    pageId?: string;
  };
}

/**
 * Result of a style clone operation
 */
export interface StyleCloneResult {
  /** Whether cloning was successful */
  success: boolean;
  /** Cloned style properties to apply */
  clonedStyles?: {
    style?: Record<string, unknown>;
    properties?: Record<string, unknown>;
    size?: { width?: number | string; height?: number | string };
  };
  /** Source component info */
  sourceComponent?: {
    id: string;
    type: string;
    name?: string;
  };
  /** Warnings about incompatible properties that were skipped */
  warnings: string[];
  /** Error message if unsuccessful */
  error?: string;
  /** Property names that were skipped due to incompatibility */
  skippedProperties?: string[];
}

/**
 * Parsed clone command from natural language
 */
export interface ParsedCloneCommand {
  /** The source reference extracted from the command */
  sourceRef: string;
  /** The scope of styles to clone */
  scope: StyleCloneScope;
  /** Original command text */
  originalCommand: string;
}

// ============================================================================
// Phase 3.1: Breakpoint-Specific Commands Types
// ============================================================================

/**
 * Valid breakpoint identifiers for responsive design
 */
export type BreakpointId = 'mobile' | 'tablet' | 'desktop' | 'all';

/**
 * Responsive style changes for specific breakpoints
 */
export interface ResponsiveStyleChanges {
  mobile?: Record<string, unknown>;
  tablet?: Record<string, unknown>;
  desktop?: Record<string, unknown>;
}

/**
 * Extended PropertyChange with breakpoint support
 * When breakpoint is set, changes apply only to that specific breakpoint
 */
export interface BreakpointPropertyChange {
  /** Target breakpoint for this change (undefined = all breakpoints) */
  breakpoint?: BreakpointId;
  /** Styles to apply at specific breakpoints */
  responsiveStyles?: ResponsiveStyleChanges;
  /** Style changes */
  style?: Record<string, unknown>;
  /** Property changes */
  properties?: Record<string, unknown>;
  /** Size changes */
  size?: { width?: number | string; height?: number | string };
  /** Position changes */
  position?: { x: number; y: number; z?: number };
  /** Data changes (for charts) */
  data?: unknown[];
  /** Component type changes */
  type?: string;
}

/**
 * Breakpoint aliases for natural language support
 * Maps colloquial terms to canonical breakpoint IDs
 */
export const BREAKPOINT_ALIASES: Record<string, BreakpointId> = {
  // Mobile aliases
  'mobile': 'mobile',
  'phone': 'mobile',
  'phones': 'mobile',
  'small screens': 'mobile',
  'small screen': 'mobile',
  'small': 'mobile',
  'xs': 'mobile',
  'sm': 'mobile',
  'smartphone': 'mobile',
  'smartphones': 'mobile',

  // Tablet aliases
  'tablet': 'tablet',
  'tablets': 'tablet',
  'ipad': 'tablet',
  'ipads': 'tablet',
  'medium': 'tablet',
  'md': 'tablet',
  'medium screens': 'tablet',
  'medium screen': 'tablet',

  // Desktop aliases
  'desktop': 'desktop',
  'desktops': 'desktop',
  'computer': 'desktop',
  'computers': 'desktop',
  'laptop': 'desktop',
  'laptops': 'desktop',
  'large screens': 'desktop',
  'large screen': 'desktop',
  'large': 'desktop',
  'lg': 'desktop',
  'xl': 'desktop',
  'big screens': 'desktop',
  'big screen': 'desktop',

  // All breakpoints
  'all': 'all',
  'everywhere': 'all',
  'all screens': 'all',
  'all devices': 'all',
};

/**
 * Parse a breakpoint reference from natural language
 * Returns the canonical breakpoint ID or undefined if not recognized
 */
export function parseBreakpointReference(text: string): BreakpointId | undefined {
  const normalized = text.toLowerCase().trim();
  return BREAKPOINT_ALIASES[normalized];
}

/**
 * Get human-readable name for a breakpoint
 */
export function getBreakpointDisplayName(breakpoint: BreakpointId): string {
  const names: Record<BreakpointId, string> = {
    mobile: 'Mobile',
    tablet: 'Tablet',
    desktop: 'Desktop',
    all: 'All Devices',
  };
  return names[breakpoint] || breakpoint;
}

// ============================================================================
// Phase 3.2: Multi-Select Operations Types
// ============================================================================

/**
 * Types of alignment operations
 */
export type AlignmentAxis = 'left' | 'right' | 'top' | 'bottom' | 'center-h' | 'center-v';

/**
 * Types of distribution operations
 */
export type DistributionAxis = 'horizontal' | 'vertical';

/**
 * Dimension matching types
 */
export type MatchDimension = 'width' | 'height' | 'both';

/**
 * Reference for size matching
 */
export type MatchReference = 'largest' | 'smallest' | 'first' | 'average';

/**
 * Multi-select operation types
 */
export type MultiSelectOperationType = 'align' | 'distribute' | 'match' | 'stack' | 'group';

/**
 * Position change for a single component
 */
export interface MultiSelectPositionChange {
  id: string;
  x?: number;
  y?: number;
  deltaX?: number;
  deltaY?: number;
}

/**
 * Size change for a single component
 */
export interface MultiSelectSizeChange {
  id: string;
  width?: number | string;
  height?: number | string;
}

/**
 * Result of a multi-select operation
 */
export interface MultiSelectOperationResult {
  success: boolean;
  positionChanges?: MultiSelectPositionChange[];
  sizeChanges?: MultiSelectSizeChange[];
  affectedComponents: string[];
  skippedComponents?: Array<{ id: string; reason: string }>;
  description: string;
}

/**
 * Multi-select operation configuration
 */
export interface MultiSelectOperation {
  type: MultiSelectOperationType;
  axis?: AlignmentAxis | DistributionAxis;
  dimension?: MatchDimension;
  spacing?: number;
  reference?: MatchReference;
}

// ============================================================================
// Phase 5: Text Selection & Quoted Content Enhancement Types
// ============================================================================

/**
 * Phase 5: Text selection context for targeted text modifications
 * Used when a user selects a portion of text in the viewport and applies AI commands
 */
export interface TextSelectionContext {
  /** Starting character index of selection (0-based) */
  startIndex: number;
  /** Ending character index of selection (exclusive) */
  endIndex: number;
  /** The selected text string */
  selectedText: string;
  /** Full text content for context */
  fullText: string;
  /** Whether this is a cursor position (startIndex === endIndex) vs selection */
  isCursor: boolean;
  /** Optional: The component ID this selection belongs to */
  componentId?: string;
}

/**
 * Phase 5: Rich text segment for styled text portions
 * When selection-based modifications are applied, text becomes segmented
 */
export interface TextSegment {
  /** The text content of this segment */
  text: string;
  /** Styles applied to this segment (optional, inherits from parent if not set) */
  style?: {
    fontWeight?: string | number;
    fontStyle?: string;
    textDecoration?: string;
    textDecorationLine?: string;
    textDecorationStyle?: string;
    textDecorationColor?: string;
    color?: string;
    backgroundColor?: string;
    fontSize?: number | string;
    fontFamily?: string;
    letterSpacing?: string | number;
    textTransform?: string;
    textShadow?: string;
    opacity?: number | string;
  };
}

/**
 * Phase 5: Extended text content that supports both plain and rich text
 * - string: Plain text content (backward compatible)
 * - TextSegment[]: Rich text with per-segment styling
 */
export type TextContent = string | TextSegment[];

/**
 * Phase 5: Text operation types for selection-based modifications
 */
export type TextOperationType = 'replace' | 'insert' | 'delete' | 'style-range';

/**
 * Phase 5: Utility function to check if content is rich text
 */
export function isRichTextContent(content: unknown): content is TextSegment[] {
  return Array.isArray(content) &&
    content.length > 0 &&
    typeof (content[0] as TextSegment)?.text === 'string';
}

/**
 * Phase 5: Convert plain text to a single-segment rich text
 */
export function plainTextToSegments(text: string): TextSegment[] {
  return [{ text }];
}

/**
 * Phase 5: Convert rich text segments back to plain text
 */
export function segmentsToPlainText(segments: TextSegment[]): string {
  return segments.map(s => s.text).join('');
}

/**
 * Phase 5: Merge adjacent segments with identical styles
 * Reduces segment count for cleaner rendering
 */
export function mergeAdjacentSegments(segments: TextSegment[]): TextSegment[] {
  if (segments.length === 0) return [];

  const result: TextSegment[] = [{ ...segments[0] }];

  for (let i = 1; i < segments.length; i++) {
    const current = segments[i];
    const previous = result[result.length - 1];

    // Check if styles are identical (or both undefined)
    const currentStyleStr = JSON.stringify(current.style || {});
    const previousStyleStr = JSON.stringify(previous.style || {});

    if (currentStyleStr === previousStyleStr) {
      // Merge segments
      previous.text += current.text;
    } else {
      // Add as new segment
      result.push({ ...current });
    }
  }

  return result;
}

/**
 * Phase 5: Apply styles to a text range and return segmented result
 * @param fullText The complete text content
 * @param startIndex Start of range to style
 * @param endIndex End of range to style (exclusive)
 * @param styles Styles to apply to the range
 * @param existingSegments Optional existing segments (for layered styling)
 */
export function applyStylesToRange(
  fullText: string,
  startIndex: number,
  endIndex: number,
  styles: NonNullable<TextSegment['style']>,
  existingSegments?: TextSegment[]
): TextSegment[] {
  console.log('[Phase 5.2] Applying styles to range:', { startIndex, endIndex, styles });

  // If no existing segments, create from plain text
  if (!existingSegments || existingSegments.length === 0) {
    const segments: TextSegment[] = [];

    // Before selection
    if (startIndex > 0) {
      segments.push({ text: fullText.slice(0, startIndex) });
    }

    // Selected portion with styles
    segments.push({
      text: fullText.slice(startIndex, endIndex),
      style: styles
    });

    // After selection
    if (endIndex < fullText.length) {
      segments.push({ text: fullText.slice(endIndex) });
    }

    return mergeAdjacentSegments(segments);
  }

  // Handle existing segments - need to split and merge appropriately
  // This is more complex: find affected segments and split them
  const result: TextSegment[] = [];
  let currentPos = 0;

  for (const segment of existingSegments) {
    const segmentEnd = currentPos + segment.text.length;

    if (segmentEnd <= startIndex || currentPos >= endIndex) {
      // Segment is entirely outside the selection range
      result.push({ ...segment });
    } else if (currentPos >= startIndex && segmentEnd <= endIndex) {
      // Segment is entirely within the selection range
      result.push({
        text: segment.text,
        style: { ...segment.style, ...styles }
      });
    } else if (currentPos < startIndex && segmentEnd > endIndex) {
      // Selection is entirely within this segment - need 3 parts
      result.push({
        text: segment.text.slice(0, startIndex - currentPos),
        style: segment.style
      });
      result.push({
        text: segment.text.slice(startIndex - currentPos, endIndex - currentPos),
        style: { ...segment.style, ...styles }
      });
      result.push({
        text: segment.text.slice(endIndex - currentPos),
        style: segment.style
      });
    } else if (currentPos < startIndex) {
      // Selection starts within this segment
      result.push({
        text: segment.text.slice(0, startIndex - currentPos),
        style: segment.style
      });
      result.push({
        text: segment.text.slice(startIndex - currentPos),
        style: { ...segment.style, ...styles }
      });
    } else {
      // Selection ends within this segment
      result.push({
        text: segment.text.slice(0, endIndex - currentPos),
        style: { ...segment.style, ...styles }
      });
      result.push({
        text: segment.text.slice(endIndex - currentPos),
        style: segment.style
      });
    }

    currentPos = segmentEnd;
  }

  return mergeAdjacentSegments(result);
}
